import React, { useMemo, useState } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaEnvelopeOpenText,
  FaPlus,
} from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useFirmUsersData } from "../../../../Hooks/useFirmUsersData";
import { useDebounce } from "../../../../Hooks/useDebounce";
import { useCreateFirmUserMutation, useGetRoleTemplatesQuery, useUpdateUserMutation } from "../../../../store";
import { useAlert } from "../../../../context/AlertContext";
import RoleTemplateDropdown from "../../../User/RoleTemplateDropdown";
import UserAutoReportDrawer from "../UserAutoReportDrawer";
import { UserCreateDrawer } from "../../../Utils/UserDrawer";
import { MascotLoadingOverlay, EmptyState, SearchInput, AppLink, FilterTabs } from "../../../shared";
import { getRoleLabel, getRoleBadgeClass } from "../../../../utils/roleBadge";

// Rol etiketi/rengi ortak util'den gelir — subRole artık şablon adıdır ve
// sabit sözlükle eşleşmeyebilir (özel adlı şablonlar da renk alsın).
const getRoleBadgeColor = (role, subRole) => getRoleBadgeClass(role, subRole);

// Rolü firma rol şablonundan gelen kullanıcılar — listede rol buradan değişir.
const TEMPLATE_ROLES = ["storage", "dealer", "customer"];
const AUTO_REPORT_USER_ROLES = ["customer", "dealer"];

// "Tümü" sekmesinin id'si — rol etiketleriyle çakışmasın diye etiket olamaz.
const ALL_ROLES = "__all__";

// extraFilter: şablon sütunundan gelen süzgeç (#298). hideRoleFilter: sütun
// varken rol çipleri aynı işi yaptığı için gizlenir (rol etiketi = şablon adı).
const UsersTab = ({ firm, externalData, readOnly = false, extraFilter, hideRoleFilter = false }) => {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  // readOnly: bayi bağlamında /Anasayfa/Kullanıcı/:userId route'u dealer
  // rolüne hiç açık değil (App.js — sadece Storage+Admin), o yüzden dealer
  // wrapper'ı burayı readOnly=true ile çağırır ve satır tıklanamaz olur.
  const canViewUserDetail = !readOnly && hasPermission("users.detail");
  const createFirmUserMutation = useCreateFirmUserMutation();
  const [search, setSearch] = useState("");
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const internalData = useFirmUsersData(
    externalData ? { id: null, token: null } : { id: firm?._id, token: auth.accessToken }
  );
  const { users, isLoading, hasNoData, refetch } = externalData || internalData;
  const [reportDrawerUser, setReportDrawerUser] = useState(null);

  // Rol değiştirme (#168) — kullanıcı detayındaki Rol kartıyla aynı yetki
  // (users.edit) ve aynı istek: roleTemplate gönderilir, server subRole +
  // izinleri şablondan uygular. Bayi bağlamında (readOnly) yok.
  const canEditRole = !readOnly && hasPermission("users.edit");
  const { data: templatesRes, isFetching: templatesLoading } = useGetRoleTemplatesQuery(
    { token: auth.accessToken, id: firm?._id },
    { skip: !canEditRole || !firm?._id || !auth.accessToken }
  );
  const roleTemplates = templatesRes?.data?.templates || [];
  const [updateUser] = useUpdateUserMutation();
  const [savingRoleUserId, setSavingRoleUserId] = useState(null);
  const { showSuccess, showError } = useAlert();
  const canChangeRoleOf = (user) => canEditRole && TEMPLATE_ROLES.includes(user.role);

  const handleRoleChange = async (user, templateId, template) => {
    setSavingRoleUserId(user._id);
    try {
      await updateUser({
        input: { id: user._id, role: user.role, roleTemplate: templateId },
        token: auth.accessToken,
      }).unwrap();
      showSuccess(`${user.name} ${user.lastName} rolü "${template?.name || "yeni rol"}" olarak değiştirildi.`);
      refetch?.();
    } catch (err) {
      showError(err?.data?.message || "Rol değiştirilemedi.");
    }
    setSavingRoleUserId(null);
  };
  // Seçili roller (rol etiketi). Boş = Tümü. Birden fazla rol seçilebilir.
  const [selectedRoles, setSelectedRoles] = useState([]);

  // Rol sekmeleri firmadaki kullanıcılardan türetilir — subRole şablon adı
  // olduğu için sabit liste yok; filtre de görünen etikete göre çalışır.
  const roleFilters = useMemo(() => {
    const counts = new Map();
    users.forEach((u) => {
      const label = getRoleLabel(u.role, u.subRole);
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return [
      { id: ALL_ROLES, label: "Tümü", count: users.length },
      ...[...counts.entries()]
        .sort(([a], [b]) => a.localeCompare(b, "tr"))
        .map(([label, count]) => ({ id: label, label, count })),
    ];
  }, [users]);

  const toggleRole = (id) => {
    if (id === ALL_ROLES) return setSelectedRoles([]);
    setSelectedRoles((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  // Arama gecikmeli (#407) — her tuşta liste yeniden süzülüp çizilmesin.
  const debouncedSearch = useDebounce(search, 250);
  const filteredUsers = useMemo(() => {
    const s = debouncedSearch.toLowerCase();
    return users.filter((user) => {
      if (extraFilter && !extraFilter(user)) return false;
      if (
        selectedRoles.length > 0 &&
        !selectedRoles.includes(getRoleLabel(user.role, user.subRole))
      )
        return false;
      return (
        user.name?.toLowerCase().includes(s) ||
        user.lastName?.toLowerCase().includes(s) ||
        user.email?.toLowerCase().includes(s) ||
        user.tel?.toLowerCase().includes(s)
      );
    });
  }, [users, extraFilter, selectedRoles, debouncedSearch]);

  // Otomatik rapor yalnızca müşteri kullanıcıları için — listede müşteri yoksa
  // sütun gizlenir. Çekmece yalnız merkez uçlarını kullanır (getUser: users.read, kayıt:
  // users.editAutoReport); bayi bağlamında (readOnly) ve izinsizde gizli —
  // önceden buton görünüp çekmece 403 ile "yüklenemedi" diyordu.
  const canEditUserAutoReport =
    !readOnly && hasPermission("users.read") && hasPermission("users.editAutoReport");
  // Otomatik rapor müşteri ve (#209) bayi kullanıcılarına kurulabilir; bayide
  // rapor bayi ağını kapsar.
  const canHaveAutoReport = (u) => AUTO_REPORT_USER_ROLES.includes(u.role);
  const hasCustomerUsers =
    canEditUserAutoReport && filteredUsers.some(canHaveAutoReport);

  return (
    <div className="flex flex-col h-full w-full">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shrink-0 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
            <FaUser className="w-4 h-4 text-indigo-500" />
          </div>
          <div className="shrink-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-800">Kullanıcılar</h2>
            <p className="text-xs text-gray-400">{filteredUsers.length} kullanıcı kayıtlı</p>
          </div>
          <SearchInput
            placeholder="İsim, email veya telefon ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName="flex-1 min-w-0 ml-auto max-w-md"
          />
          {hasPermission("firms.createUser") && (
            <button
              type="button"
              onClick={() => setCreateDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors shrink-0"
            >
              <FaPlus className="w-3 h-3" />
              <span className="hidden sm:inline">Kullanıcı Oluştur</span>
            </button>
          )}
        </div>
        {/* Rol filtresi — birden fazla rol seçilebilir; yalnız 2+ farklı rol varsa anlamlı */}
        {!hideRoleFilter && roleFilters.length > 2 && (
          <div className="mt-3 -mb-3 sm:-mb-4">
            <FilterTabs
              multiple
              filters={roleFilters}
              activeFilter={selectedRoles.length ? selectedRoles : [ALL_ROLES]}
              onChange={toggleRole}
            />
          </div>
        )}
      </div>

      <UserCreateDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        token={auth.accessToken}
        refetch={refetch}
        defaultFirm={{ firmName: firm?.firmName, firmType: firm?.firmType, _id: firm?._id }}
        mutation={createFirmUserMutation}
      />

      <UserAutoReportDrawer
        open={!!reportDrawerUser}
        onClose={() => setReportDrawerUser(null)}
        user={reportDrawerUser}
        token={auth.accessToken}
        onSuccess={() => refetch()}
      />

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <MascotLoadingOverlay message="Kullanıcılar yükleniyor..." />
          </div>
        ) : filteredUsers.length === 0 ? (
          <EmptyState
            message="Kullanıcı Bulunamadı"
            suggestion={
              search || selectedRoles.length
                ? "Arama veya rol filtresini değiştirmeyi deneyin."
                : extraFilter && users.length > 0
                  ? "Bu şablonda kullanıcı yok."
                  : "Bu firmaya henüz kullanıcı eklenmemiş."
            }
          />
        ) : (
          <>
            {/* Mobil: Satır bazlı liste */}
            <div className="sm:hidden divide-y divide-gray-100">
              {filteredUsers.map((user) => {
                // Satır link değil — yalnızca avatar + isim + e-posta bloğu linktir,
                // hover'da altı çizilir.
                const mobileUserInner = (
                  <>
                    <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center shrink-0 transition-all group-hover:ring-2 group-hover:ring-blue-300">
                      <FaUser className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-700 group-hover:underline underline-offset-2">{user.name} {user.lastName}</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                        {user.email && <span className="truncate group-hover:underline underline-offset-2">{user.email}</span>}
                      </div>
                    </div>
                  </>
                );
                return (
                <div
                  key={user._id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50"
                >
                  {canViewUserDetail ? (
                    <AppLink
                      to={`/Anasayfa/Kullanıcı/${user._id}`}
                      state={{ user, fromPage: "Firma", firmId: firm?._id }}
                      className="group flex flex-1 items-center gap-3 min-w-0"
                      title="Kullanıcıya git"
                    >
                      {mobileUserInner}
                    </AppLink>
                  ) : (
                    <div className="flex flex-1 items-center gap-3 min-w-0">{mobileUserInner}</div>
                  )}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Otomatik rapor yalnızca müşteri kullanıcıları içindir */}
                    {canEditUserAutoReport && canHaveAutoReport(user) && (
                      <button
                        type="button"
                        onClick={() => setReportDrawerUser(user)}
                        className="p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100"
                        title="Otomatik rapor ayarları"
                      >
                        <FaEnvelopeOpenText className="w-4 h-4" />
                      </button>
                    )}
                    {canChangeRoleOf(user) ? (
                      <RoleTemplateDropdown
                        className="w-36"
                        templates={roleTemplates}
                        value={user.roleTemplate ? String(user.roleTemplate) : ""}
                        displayValue={getRoleLabel(user.role, user.subRole)}
                        loading={templatesLoading || savingRoleUserId === user._id}
                        disabled={savingRoleUserId === user._id}
                        onChange={(id, t) => handleRoleChange(user, id, t)}
                      />
                    ) : (
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold ${getRoleBadgeColor(user.role, user.subRole)}`}>
                        {getRoleLabel(user.role, user.subRole)}
                      </span>
                    )}
                    {user.isActive !== undefined && (
                      <span className={`w-2 h-2 rounded-full ${user.isActive ? "bg-green-400" : "bg-red-400"}`} />
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Desktop: Kompakt tablo */}
            <div className="hidden sm:block">
              <table className="w-full">
                {/* z-20: satır içi pozisyonlu hücreler scroll'da header'ın üstüne binmesin */}
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    {/* Otomatik Rapor sütunu yalnızca müşteri kullanıcısı varsa gösterilir */}
                    {["Kullanıcı", "İletişim", "Rol", "Durum", ...(hasCustomerUsers ? ["Otomatik Rapor"] : [])].map((h, i) => (
                      <th key={i} className={`px-4 py-2.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 ${h === "Otomatik Rapor" ? "text-center w-32" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user._id}
                      className="transition-colors hover:bg-gray-50/50"
                    >
                      <td className="px-4 py-3">
                        {/* Satır link değil — yalnızca avatar + isim + ID linktir, hover'da altı çizilir */}
                        {(() => {
                          const userInner = (
                            <>
                              <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center shrink-0 transition-all group-hover:ring-2 group-hover:ring-blue-300">
                                <FaUser className="w-3 h-3 text-blue-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 group-hover:underline underline-offset-2">{user.name} {user.lastName}</p>
                                <p className="text-[11px] text-gray-400 group-hover:underline underline-offset-2">ID: {user._id?.slice(-8)}</p>
                              </div>
                            </>
                          );
                          return canViewUserDetail ? (
                            <AppLink
                              to={`/Anasayfa/Kullanıcı/${user._id}`}
                              state={{ user, fromPage: "Firma", firmId: firm?._id }}
                              className="group flex items-center gap-2.5 w-fit max-w-full"
                              title="Kullanıcıya git"
                            >
                              {userInner}
                            </AppLink>
                          ) : (
                            <div className="flex items-center gap-2.5">{userInner}</div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          {user.email && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <FaEnvelope className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                              <span className="truncate">{user.email}</span>
                            </div>
                          )}
                          {user.tel && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-600">
                              <FaPhone className="w-2.5 h-2.5 text-gray-400 shrink-0" />
                              <span>{user.tel}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {canChangeRoleOf(user) ? (
                          <RoleTemplateDropdown
                            templates={roleTemplates}
                            value={user.roleTemplate ? String(user.roleTemplate) : ""}
                            displayValue={getRoleLabel(user.role, user.subRole)}
                            loading={templatesLoading || savingRoleUserId === user._id}
                            disabled={savingRoleUserId === user._id}
                            onChange={(id, t) => handleRoleChange(user, id, t)}
                          />
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold ${getRoleBadgeColor(user.role, user.subRole)}`}>
                            {getRoleLabel(user.role, user.subRole)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.isActive !== undefined ? (
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                            user.isActive ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${user.isActive ? "bg-green-500" : "bg-red-500"}`} />
                            {user.isActive ? "Aktif" : "Pasif"}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      {hasCustomerUsers && (
                        <td className="px-4 py-3 text-center">
                          {canHaveAutoReport(user) ? (
                            <div className="inline-flex items-center justify-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setReportDrawerUser(user)}
                                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100 transition-colors"
                                title="Otomatik rapor ayarları"
                              >
                                <FaEnvelopeOpenText className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

    </div>
  );
};

export default UsersTab;
